import { Spinner } from '@frontend/components/common';
import { LuTriangleAlert } from 'react-icons/lu';
import { FormattedMessage } from 'react-intl';

import { useWizardModalScreenNavigation } from '../../wizard-modal-navigation.context';
import { NivalizTransactionMethod, useTransactionMethod } from './transaction-method.controller';

const methods: { value: NivalizTransactionMethod; label: React.ReactNode; description: React.ReactNode }[] = [
    {
        value: 'coffin',
        label: (
            <FormattedMessage
                id='feature.nivaliz.transactions.type.coffin'
                description='Label for Nivaliz coffin (inbound) transaction type'
                defaultMessage='Coffin'
            />
        ),
        description: (
            <FormattedMessage
                id='wizard.transaction-method.coffin-description'
                description='Description for the coffin transaction method option'
                defaultMessage='Register an inbound transaction.'
            />
        )
    },
    {
        value: 'urn',
        label: (
            <FormattedMessage
                id='feature.nivaliz.transactions.type.urn'
                description='Label for Nivaliz urn (outbound) transaction type'
                defaultMessage='Urn'
            />
        ),
        description: (
            <FormattedMessage
                id='wizard.transaction-method.urn-description'
                description='Description for the urn transaction method option'
                defaultMessage='Register an outbound transaction.'
            />
        )
    },
    {
        value: 'other',
        label: (
            <FormattedMessage
                id='wizard.transaction-method.other'
                description='Label for the other/general Nivaliz transaction method option'
                defaultMessage='Other'
            />
        ),
        description: (
            <FormattedMessage
                id='wizard.transaction-method.other-description'
                description='Description for the other/general Nivaliz transaction method option'
                defaultMessage='Register a general transaction.'
            />
        )
    }
];

export const TransactionMethod = (props: { wizard_id: string }) => {
    const viewProps = useTransactionMethod(props);

    useWizardModalScreenNavigation({
        onContinue: viewProps.onSubmit,
        continueDisabled: viewProps.submitDisabled
    });

    return (
        <div
            className='flex h-full flex-col gap-6'
            data-functionality-id={viewProps.functionality?.id}
            data-application={viewProps.application}
            data-feature={viewProps.feature}>
            {viewProps.isNivaliz && (
                <>
                    <h2 className='text-foreground-default text-xl leading-normal font-semibold'>
                        <FormattedMessage
                            id='wizard.transaction-method.title'
                            defaultMessage='What are you registering?'
                        />
                    </h2>
                    <fieldset className='flex flex-col gap-3'>
                        <legend className='sr-only'>
                            <FormattedMessage
                                id='wizard.transaction-method.title'
                                defaultMessage='What are you registering?'
                            />
                        </legend>
                        {methods.map((method) => {
                            const selected = viewProps.selectedMethod === method.value;
                            return (
                                <label
                                    key={method.value}
                                    className={`bg-background-surface flex cursor-pointer items-center gap-4 rounded-md border p-4 ${
                                        selected ? 'border-brand-primary' : 'border-border-default hover:border-brand-secondary'
                                    }`}>
                                    <input
                                        type='radio'
                                        name='nivaliz-transaction-method'
                                        value={method.value}
                                        checked={selected}
                                        onChange={() => viewProps.setSelectedMethod(method.value)}
                                        className='accent-brand-primary size-5 shrink-0'
                                    />
                                    <span className='flex flex-col gap-1'>
                                        <span className='text-foreground-default font-semibold'>{method.label}</span>
                                        <span className='text-brand-muted text-sm'>{method.description}</span>
                                    </span>
                                </label>
                            );
                        })}
                    </fieldset>
                    {viewProps.isResolvingConfiguration && (
                        <div className='text-brand-muted flex items-center gap-2 text-sm'>
                            <Spinner className='size-4' />
                            <FormattedMessage
                                id='wizard.transaction-method.resolving-configuration'
                                defaultMessage='Checking the transaction configuration...'
                            />
                        </div>
                    )}
                    {viewProps.configurationError && (
                        <div className='bg-status-warning/20 border-status-warning flex items-start gap-3 rounded-md border p-4'>
                            <LuTriangleAlert className='text-status-warning size-5 shrink-0' />
                            <div className='text-foreground-default text-sm font-medium'>
                                {viewProps.configurationError === 'functionality' ? (
                                    <FormattedMessage
                                        id='wizard.transaction-method.missing-platform-functionality'
                                        description='Warning shown on the Nivaliz transaction method screen when no platform functionality exists for the selected transaction type.'
                                        defaultMessage='No platform functionality is configured for this transaction type. Please contact your administrator.'
                                    />
                                ) : (
                                    <FormattedMessage
                                        id='wizard.transaction-method.missing-workflow'
                                        defaultMessage='No transaction workflow is configured for this transaction type. Please contact your administrator.'
                                    />
                                )}
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};
